import {match} from 'ts-pattern';

import {any, isEmpty, isNil} from 'ramda';

import {TelephonyLine, TelephonyLineTwilioNumber} from '@dms/api/settings';

/**
 * Line state is always derived from the attached numbers, never stored: the backend reports the
 * live Twilio lookup state of every number and the last inbound call that reached it on each
 * read. Drift on any number (`missing`) outranks a merely unreachable provider (`unavailable`),
 * which outranks a number no call has reached yet. A line is active only once every number has
 * taken a call, because an attached number whose voice URL never pointed at Omnetic looks
 * identical to a working one until a call proves it.
 */
export type TelephonyLineState =
  | 'ACTIVE'
  | 'IN_SETUP'
  | 'NUMBER_MISSING'
  | 'NUMBER_UNKNOWN'
  | 'AWAITING_FIRST_CALL';

const hasLookupStatus =
  (lookupStatus: TelephonyLineTwilioNumber['lookupStatus']) =>
  (numbers: TelephonyLineTwilioNumber[]) =>
    any((number) => number.lookupStatus === lookupStatus, numbers);

const hasNumberWithoutCall = (numbers: TelephonyLineTwilioNumber[]) =>
  any((number) => isNil(number.lastInboundCallAt), numbers);

export const getTelephonyLineState = (
  line: Pick<TelephonyLine, 'twilioNumbers'>
): TelephonyLineState =>
  match(line.twilioNumbers)
    .when(isEmpty, () => 'IN_SETUP' as const)
    .when(hasLookupStatus('missing'), () => 'NUMBER_MISSING' as const)
    .when(hasLookupStatus('unavailable'), () => 'NUMBER_UNKNOWN' as const)
    .when(hasNumberWithoutCall, () => 'AWAITING_FIRST_CALL' as const)
    .otherwise(() => 'ACTIVE' as const);
